"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const forEach_1 = __importDefault(require("lodash/forEach"));
const isEmpty_1 = __importDefault(require("lodash/isEmpty"));
const keys_1 = __importDefault(require("lodash/keys"));
const has_1 = __importDefault(require("lodash/has"));
const eventemitter3_1 = __importDefault(require("eventemitter3"));
const functional_red_black_tree_1 = __importDefault(require("functional-red-black-tree"));
const compare_1 = require("@zedoc/compare");
const SharedResource_1 = __importDefault(require("./SharedResource"));
const utils_1 = require("./utils");
class ResourcesManager extends eventemitter3_1.default {
    constructor({ resourcesFactory, cleanupDelay, cleanupDelayOnError } = {}) {
        super();
        this.counter = 0;
        this.resourcesTree = (0, functional_red_black_tree_1.default)(compare_1.compare);
        this.listeners = {};
        this.resourcesFactory = resourcesFactory;
        this.cleanupDelay = cleanupDelay;
        this.cleanupDelayOnError = cleanupDelayOnError;
    }
    nextUniqueId() {
        this.counter += 1;
        return this.counter.toString();
    }
    cleanupResources() {
        this.resourcesTree.forEach((params, resource) => {
            resource.maybeCleanup();
        });
    }
    getCleanupDelay(request) {
        if (typeof this.cleanupDelay === 'function') {
            return this.cleanupDelay(request);
        }
        if (typeof this.cleanupDelay === 'number') {
            return this.cleanupDelay;
        }
        return 0;
    }
    getCleanupDelayOnError(request) {
        if (typeof this.cleanupDelayOnError === 'function') {
            return this.cleanupDelayOnError(request);
        }
        if (typeof this.cleanupDelayOnError === 'number') {
            return this.cleanupDelayOnError;
        }
        return 0;
    }
    getOrCreateResource(request) {
        let resource = this.resourcesTree.get(request);
        if (!resource) {
            const id = this.nextUniqueId();
            const requestMeta = { id };
            this.emit('create', { id, request });
            resource = new SharedResource_1.default({
                create: (cb) => {
                    let handle = this.resourcesFactory(request, requestMeta, (0, utils_1.once)((error, value) => {
                        if (!error) {
                            this.emit('ready', { id, value });
                        }
                        else {
                            console.error(`While requesting resource id ${id}`, request, error);
                            this.emit('error', { id, error });
                        }
                        cb(error, value);
                    }));
                    return (refreshOnly) => {
                        if (handle) {
                            handle.stop();
                            handle = null;
                        }
                        if (!refreshOnly) {
                            this.resourcesTree = this.resourcesTree.remove(request);
                            this.emit('delete', { id, request });
                        }
                    };
                },
                cleanupDelay: this.getCleanupDelay(request),
                cleanupDelayOnError: this.getCleanupDelayOnError(request),
            });
            resource.id = id;
            resource.request = request;
            this.resourcesTree = this.resourcesTree.insert(request, resource);
        }
        return { id: resource.id, resource };
    }
    getOrCreateListener(id) {
        const listener = this.listeners[id] || {
            byResourceId: {},
        };
        if (!this.listeners[id]) {
            this.listeners[id] = listener;
        }
        return listener;
    }
    refresh(request) {
        const resource = this.resourcesTree.get(request);
        if (!resource) {
            return Promise.reject(new Error(`Unknown resource ${request ? request.name : '[NO_NAME]'}`));
        }
        return resource.refresh();
    }
    updateRequests(listenerId, requests) {
        const nextResources = {};
        const listener = this.getOrCreateListener(listenerId);
        (0, forEach_1.default)(requests, (request) => {
            if (!request) {
                return;
            }
            const { id, resource } = this.getOrCreateResource(request);
            if (!listener.byResourceId[id]) {
                listener.byResourceId[id] = resource.require();
                listener.byResourceId[id].promise.catch(() => {
                    // we are only catching the promise here to prevent uncaught promise warning
                });
            }
            nextResources[id] = listener.byResourceId[id];
        });
        (0, forEach_1.default)(listener.byResourceId, ({ release }, id) => {
            if (!(0, has_1.default)(nextResources, id)) {
                release();
                delete listener.byResourceId[id];
            }
        });
        if ((0, isEmpty_1.default)(nextResources)) {
            delete this.listeners[listenerId];
        }
        return (0, keys_1.default)(nextResources);
    }
}
exports.default = ResourcesManager;
//# sourceMappingURL=ResourcesManager.js.map